<template>
  <div>
    <v-autocomplete
      ref="autocomplete"
      :value="autocompleteValue"
      :items="displayItems"
      :search-input.sync="search"
      :label="label"
      :disabled="disabled"
      :loading="isLoadingOptions"
      :error-messages="errorMessages"
      :multiple="multiple"
      :chips="multiple"
      :small-chips="multiple"
      :hide-selected="multiple"
      :clearable="clearable"
      :outlined="outlined"
      :dense="dense"
      :solo="solo"
      :flat="flat"
      :hide-details="hideDetails"
      item-text="value"
      item-value="key"
      return-object
      :menu-props="{ closeOnContentClick: false, maxHeight: 320, offsetY: true }"
      @input="handleInput"
    >
      <template v-if="canAddTag" #prepend-item>
        <v-list-item @mousedown.prevent @click="openAddDialog">
          <v-list-item-icon>
            <v-icon>mdi-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t("FIELD_TAGS.ADD_NEW") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
      </template>
      <template v-if="multiple" #selection="data">
        <FieldTagChip
          :tag="data.item"
          :closable="!disabled"
          @close="removeTag(data.item)"
        />
      </template>
    </v-autocomplete>

    <v-dialog v-model="showAddDialog" max-width="480" persistent>
      <v-card :loading="isCreating" :disabled="isCreating">
        <v-card-title>{{ $t("FIELD_TAGS.ADD_NEW") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="createTag">
            <v-text-field
              v-model="newTagValue"
              :label="`${$t('GENERAL.LABELS.NAME')} *`"
              :error-messages="addError"
              autofocus
              @input="addError = ''"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="closeAddDialog">{{ $t("FIELD_TAGS.CANCEL_BUTTON") }}</v-btn>
          <v-btn color="success" :loading="isCreating" @click="createTag">
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { GET_FIELD_TAG_OPTIONS, STORE_FIELD_TAG } from "@/store/fieldTags.module";
import { SHOW_SNACK } from "@/store/snack.module";
import FieldTagChip from "@/views/partials/fieldTags/FieldTagChip.vue";

export default {
  name: "FieldTagsInput",
  components: {
    FieldTagChip,
  },
  props: {
    value: {
      type: [Array, Number, String],
      default: null,
    },
    fieldTagTypeSlug: {
      type: String,
      default: "",
    },
    fieldTagTypeId: {
      type: [Number, String],
      default: null,
    },
    valueMode: {
      type: String,
      default: "object",
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [Array, String],
      default: () => [],
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      suggestions: [],
      resolvedTypeId: this.fieldTagTypeId,
      isLoadingOptions: false,
      isCreating: false,
      showAddDialog: false,
      newTagValue: "",
      addError: "",
    };
  },
  computed: {
    canAddTag() {
      if (this.disabled) {
        return false;
      }

      if (this.valueMode === "id") {
        return this.hasPermissions("field-tags-create");
      }

      return true;
    },
    normalizedSelection() {
      if (this.valueMode === "id") {
        const ids = this.multiple
          ? (Array.isArray(this.value) ? this.value : []).map(Number)
          : this.value === null || this.value === undefined || this.value === ""
            ? []
            : [Number(this.value)];

        return ids
          .filter((id) => !Number.isNaN(id))
          .map((id) => {
            const found = this.suggestions.find((tag) => Number(tag.id) === id);

            return found || this.withKey({ id, value: String(id) });
          });
      }

      return (Array.isArray(this.value) ? this.value : []).map((tag) => {
        const keyed = this.withKey(tag);

        return this.suggestions.find((item) => item.key === keyed.key) || keyed;
      });
    },
    autocompleteValue() {
      if (!this.multiple) {
        return this.normalizedSelection[0] || null;
      }

      return this.normalizedSelection;
    },
    displayItems() {
      const items = new Map();

      this.suggestions.forEach((tag) => {
        if (this.isExcluded(tag)) {
          return;
        }

        items.set(tag.key, tag);
      });

      this.normalizedSelection.forEach((tag) => {
        if (!items.has(tag.key)) {
          items.set(tag.key, tag);
        }
      });

      return [...items.values()];
    },
  },
  watch: {
    fieldTagTypeSlug() {
      this.loadSuggestions();
    },
    fieldTagTypeId(id) {
      this.resolvedTypeId = id || null;
      this.loadSuggestions();
    },
  },
  mounted() {
    this.$root.$on("field-tag-created", this.handleExternalTagCreated);
    this.loadSuggestions();
  },
  beforeDestroy() {
    this.$root.$off("field-tag-created", this.handleExternalTagCreated);
  },
  methods: {
    withKey(tag) {
      const value = String(tag?.value || "").trim();
      const id = tag?.id ? Number(tag.id) : null;

      return {
        id,
        value,
        field_tag_type_id: tag?.field_tag_type_id || this.resolvedTypeId || null,
        key: id ? `id:${id}` : `value:${value.toLowerCase()}`,
      };
    },
    isExcluded(tag) {
      if (!tag?.id) {
        return false;
      }

      return this.excludeIds.map(Number).includes(Number(tag.id));
    },
    loadSuggestions() {
      const params = {};

      if (this.fieldTagTypeId) {
        params.field_tag_type_id = Number(this.fieldTagTypeId);
      } else if (this.fieldTagTypeSlug) {
        params.field_tag_type_slug = this.fieldTagTypeSlug;
      } else {
        this.suggestions = [];
        this.$emit("loaded", []);
        return Promise.resolve();
      }

      this.isLoadingOptions = true;

      return this.$store
        .dispatch(GET_FIELD_TAG_OPTIONS, params)
        .then((tags) => {
          this.resolvedTypeId = this.fieldTagTypeId || tags.fieldTagTypeId || this.resolvedTypeId;
          this.suggestions = (tags || [])
            .map((tag) => this.withKey(tag))
            .filter((tag) => tag.value);
          this.$emit("loaded", this.suggestionPayload());
        })
        .catch(() => {
          this.suggestions = [];
          this.$emit("loaded", []);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isLoadingOptions = false;
        });
    },
    suggestionPayload() {
      return this.suggestions.map((tag) => ({
        id: tag.id,
        value: tag.value,
      }));
    },
    handleExternalTagCreated(tag) {
      if (!tag?.id || Number(tag.field_tag_type_id) !== Number(this.resolvedTypeId)) {
        return;
      }

      if (this.suggestions.some((item) => Number(item.id) === Number(tag.id))) {
        return;
      }

      this.suggestions.push(this.withKey(tag));
      this.suggestions.sort((a, b) => String(a.value).localeCompare(String(b.value)));
      this.$emit("loaded", this.suggestionPayload());
    },
    openAddDialog() {
      this.newTagValue = (this.search || "").trim();
      this.addError = "";
      this.showAddDialog = true;
      this.search = "";

      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.blur();
      }
    },
    closeAddDialog() {
      this.showAddDialog = false;
      this.newTagValue = "";
      this.addError = "";
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    createTag() {
      const value = this.newTagValue.trim();

      if (!value) {
        this.addError = "Item is required";
        return;
      }

      const existing = this.suggestions.find(
        (tag) => tag.value.toLowerCase() === value.toLowerCase() && !this.isExcluded(tag)
      );

      if (existing) {
        this.selectTag(existing);
        this.closeAddDialog();
        return;
      }

      if (this.resolvedTypeId && this.hasPermissions("field-tags-create")) {
        this.isCreating = true;
        this.$store
          .dispatch(STORE_FIELD_TAG, {
            field_tag_type_id: this.resolvedTypeId,
            value,
          })
          .then((created) => {
            const tag = this.withKey({
              id: created.id,
              value: created.value,
              field_tag_type_id: created.field_tag_type_id || this.resolvedTypeId,
            });
            this.suggestions.push(tag);
            this.suggestions.sort((a, b) => String(a.value).localeCompare(String(b.value)));
            this.$emit("loaded", this.suggestionPayload());
            this.$root.$emit("field-tag-created", tag);
            this.selectTag(tag);
            this.closeAddDialog();
          })
          .catch((error) => {
            this.addError = this.getErrorMessage(error);
          })
          .finally(() => {
            this.isCreating = false;
          });
        return;
      }

      if (this.valueMode === "object") {
        this.selectTag({ id: null, value });
        this.closeAddDialog();
        return;
      }

      this.$store.dispatch(SHOW_SNACK, {
        message: this.$t("GENERAL.UNEXPECTED_ERROR"),
        type: "error",
      });
    },
    selectTag(tag) {
      const normalized = this.withKey(tag);

      if (this.valueMode === "id") {
        if (!normalized.id) {
          return;
        }

        if (this.multiple) {
          const ids = (Array.isArray(this.value) ? this.value : []).map(Number);

          if (!ids.includes(normalized.id)) {
            this.$emit("input", [...ids, normalized.id]);
            this.$emit("tag-added", { id: normalized.id, value: normalized.value });
          }

          return;
        }

        this.$emit("input", normalized.id);
        this.$emit("tag-added", { id: normalized.id, value: normalized.value });
        return;
      }

      const current = (Array.isArray(this.value) ? this.value : []).map((item) => this.withKey(item));

      if (current.some((item) => item.key === normalized.key)) {
        return;
      }

      this.$emit("input", [...current, normalized].map(({ id, value: tagValue }) => ({
        id,
        value: tagValue,
      })));
      this.$emit("tag-added", { id: normalized.id, value: normalized.value });
    },
    removeTag(tag) {
      const target = this.withKey(tag);
      const current = Array.isArray(this.value) ? this.value : [];

      if (this.valueMode === "id") {
        this.$emit(
          "input",
          current.map(Number).filter((id) => id !== Number(target.id))
        );
        return;
      }

      this.$emit(
        "input",
        current
          .map((item) => this.withKey(item))
          .filter((item) => item.key !== target.key)
          .map(({ id, value }) => ({ id, value }))
      );
    },
    handleInput(selected) {
      const items = this.multiple ? selected || [] : selected ? [selected] : [];
      const tags = items
        .map((item) => this.withKey(item))
        .filter((tag) => tag.value || tag.id);

      const unique = [];
      tags.forEach((tag) => {
        if (!unique.some((item) => item.key === tag.key)) {
          unique.push(tag);
        }
      });

      if (this.valueMode === "id") {
        const ids = unique.map((tag) => Number(tag.id)).filter(Boolean);
        const nextValue = this.multiple ? ids : ids[0] || null;

        if (this.isSameIdValue(nextValue)) {
          return;
        }

        this.$emit("input", nextValue);
        return;
      }

      const previous = (Array.isArray(this.value) ? this.value : []).map((tag) => this.withKey(tag));
      const nextTags = unique.map(({ id, value }) => ({ id, value }));

      if (this.isSameObjectValue(previous, unique)) {
        return;
      }

      unique
        .filter((tag) => !previous.some((item) => item.key === tag.key))
        .forEach((tag) => {
          this.$emit("tag-added", { id: tag.id, value: tag.value });
        });

      this.$emit("input", nextTags);
      this.search = "";
    },
    isSameIdValue(nextValue) {
      if (this.multiple) {
        const current = (Array.isArray(this.value) ? this.value : []).map(Number);

        return current.length === nextValue.length
          && current.every((id, index) => id === nextValue[index]);
      }

      const current = this.value === null || this.value === undefined || this.value === ""
        ? null
        : Number(this.value);

      return current === nextValue;
    },
    isSameObjectValue(previous, next) {
      return previous.length === next.length
        && previous.every((tag, index) => tag.key === next[index].key);
    },
  },
};
</script>
